import { useCopilotSessionContext } from '@/features/copilot';
import { open as openSidebar } from '@/features/sidebar';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';
import { useAnalytics } from '@/services/analytics';
import type { ServicesApiEnvHealthAlert } from '@/services/api/services-api';

/**
 * Powers the "Ask agent" CTA on workspace health alert panels.
 *
 * `enabled` gates the CTA on `workspace_health_ask_agent && ai_enabled`.
 * `askAgent` tracks analytics, resets the copilot session, opens the
 * sidebar, and dispatches the alert's tailored copilot_prompt.
 */
export function useAskAgentForAlert(): {
  enabled: boolean;
  askAgent: (alert: ServicesApiEnvHealthAlert) => void;
} {
  const session = useCopilotSessionContext();
  const aiEnabled = useFeatureFlag(FeatureFlag.AiEnabled);
  const enabled = useFeatureFlag(FeatureFlag.WorkspaceHealthAskAgent) && aiEnabled;
  const { track } = useAnalytics();

  const askAgent = (alert: ServicesApiEnvHealthAlert) => {
    if (alert.copilot_prompt == null) return;
    void track('workspaceHealth', 'alertAskAgentClicked', {
      ActionType: alert.action_type,
      Severity: alert.severity,
    });
    const newSession = session.reset();
    openSidebar('copilot');
    void newSession.sendMessage(alert.copilot_prompt);
  };

  return { enabled, askAgent };
}
